/**
 * 月度真实盈亏趋势 —— 健康度轨迹
 *
 * 纯展示组件：吃 PnlMonthly[]（由页面层 /pnl 拉取后注入），画近数月「真实盈亏
 * = 净收入 − 全体账本成本」。刻意与顶部时间筛选解耦：健康度看的是多月轨迹，
 * 不是单个所选窗口。数据源与盈亏体检 headline 同口径（cost_events 全体成本）。
 */
import { useMemo } from 'react';
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';

import { Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { PnlMonthly } from '@/lib/api/reports';

interface PnlTrendStripProps {
  months: PnlMonthly[];
  className?: string;
}

interface TrendPoint {
  month: string;
  profit: number;
  net: number;
  cost: number;
}

const PROFIT_COLOR = '#16a34a';
const LOSS_COLOR = '#dc2626';

export function PnlTrendStrip({ months, className }: PnlTrendStripProps) {
  const points = useMemo<TrendPoint[]>(
    () =>
      months.map((m) => ({
        month: m.month.length > 5 ? m.month.slice(2) : m.month, // YYYY-MM → YY-MM
        profit: m.gross_profit_cny,
        net: m.net_revenue_cents / 100,
        cost: m.total_cost_cny,
      })),
    [months]
  );

  if (points.length === 0) return null;

  const latest = points[points.length - 1];
  const prev = points.length > 1 ? points[points.length - 2] : null;
  const improving = prev ? latest.profit >= prev.profit : true;

  return (
    <Card className={cn('p-5', className)}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-foreground">月度真实盈亏趋势</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            净收入 − 全体账本成本，近 {points.length} 个月 · 独立于顶部时间筛选（健康度看轨迹）
          </p>
        </div>
        <div
          className={cn(
            'flex shrink-0 items-center gap-1.5 text-sm font-semibold',
            latest.profit >= 0 ? 'text-success' : 'text-destructive'
          )}
        >
          {improving ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          {formatSignedCny(latest.profit)}
        </div>
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={52}
              tickFormatter={(value) => `¥${value}`}
            />
            <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.3} />
            <Tooltip content={<TrendTooltip />} cursor={{ fill: 'transparent' }} />
            <Bar dataKey="profit" radius={[3, 3, 0, 0]}>
              {points.map((point) => (
                <Cell key={point.month} fill={point.profit >= 0 ? PROFIT_COLOR : LOSS_COLOR} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function TrendTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: TrendPoint }> }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card p-2.5 text-xs shadow-elevation-1">
      <div className="mb-1 font-semibold text-foreground">{point.month}</div>
      <div className="text-muted-foreground">
        净收入 <span className="ml-1 font-mono text-foreground">{formatCny(point.net)}</span>
      </div>
      <div className="text-muted-foreground">
        总成本 <span className="ml-1 font-mono text-foreground">{formatCny(point.cost)}</span>
      </div>
      <div className="text-muted-foreground">
        真实盈亏{' '}
        <span className={cn('ml-1 font-mono', point.profit >= 0 ? 'text-success' : 'text-destructive')}>
          {formatSignedCny(point.profit)}
        </span>
      </div>
    </div>
  );
}

function formatCny(value: number) {
  const sign = value < 0 ? '-' : '';
  return `${sign}¥${Math.abs(value).toFixed(2)}`;
}

function formatSignedCny(value: number) {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}¥${Math.abs(value).toFixed(2)}`;
}
